import React from 'react';
import { RED, INK, GREY, LIGHT, TEAL, BORDER, wrap, Hero, Crumbs, CtaBand } from '../seo/SeoUI';
import { GUIDE_PAGES, GUIDES_CTA } from './data';
import EmailCaptureCard from './EmailCaptureCard';

// Renders one guide article. Supports the base schema (intro/costTable/sections/faqs) plus
// the Home-Care extensions: readTime, takeaways[], checklists[] (items = [task, cadence,
// 'diy'|'pro']), and sections[].tip (Pro tip callout).

const chipStyle = (kind) => ({
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '.4px',
  textTransform: 'uppercase',
  borderRadius: 999,
  padding: '3px 10px',
  whiteSpace: 'nowrap',
  color: kind === 'pro' ? RED : TEAL,
  background: kind === 'pro' ? 'rgba(232,35,43,.08)' : 'rgba(13,148,136,.10)',
  border: `1px solid ${kind === 'pro' ? 'rgba(232,35,43,.25)' : 'rgba(13,148,136,.30)'}`,
});

function Checklist({ list }) {
  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 16, overflow: 'hidden', marginBottom: 22 }}>
      <div style={{ padding: '18px 22px 14px', background: LIGHT, borderBottom: `1px solid ${BORDER}` }}>
        <h3 style={{ fontSize: 19, fontWeight: 800, margin: 0, color: INK }}>{list.title}</h3>
        {list.note ? <p style={{ fontSize: 14, color: GREY, margin: '6px 0 0', lineHeight: 1.55 }}>{list.note}</p> : null}
      </div>
      <div>
        {list.items.map(([task, cadence, kind], i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 22px', borderBottom: i < list.items.length - 1 ? `1px solid ${BORDER}` : 'none', background: '#fff' }}>
            <span aria-hidden="true" style={{ flex: 'none', width: 22, height: 22, borderRadius: 7, border: `2px solid ${kind === 'pro' ? RED : TEAL}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: kind === 'pro' ? RED : TEAL, fontSize: 13, fontWeight: 900 }}>✓</span>
            <span style={{ flex: 1, fontSize: 15, color: INK, fontWeight: 600, lineHeight: 1.45 }}>{task}</span>
            <span style={{ flex: 'none', fontSize: 13, color: GREY, minWidth: 90, textAlign: 'right' }}>{cadence}</span>
            {kind === 'pro' ? (
              <a href={GUIDES_CTA} style={{ ...chipStyle('pro'), textDecoration: 'none' }} title="Book a pro for this task">Book a pro</a>
            ) : (
              <span style={chipStyle('diy')}>DIY</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Guide({ slug, data }) {
  const related = (data.related || [])
    .filter((k) => GUIDE_PAGES[k])
    .map((k) => ({ slug: k, h1: GUIDE_PAGES[k].h1 }));

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Guides', href: '/guides' },
    { label: data.service },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: data.h1,
      description: data.metaDescription,
      datePublished: '2026-06-21',
      dateModified: '2026-08-20',
      author: { '@type': 'Organization', name: 'BestHand' },
      publisher: { '@type': 'Organization', name: 'BestHand' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((b, i) => ({
        '@type': 'ListItem', position: i + 1, name: b.label, ...(b.href ? { item: b.href } : {}),
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: (data.faqs || []).map(([q, a]) => ({
        '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ];

  const metaLine = [
    'By the BestHand Team',
    data.updated ? `Updated ${data.updated}` : null,
    data.readTime ? `${data.readTime} min read` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div style={{ background: '#fff' }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Hero
        breadcrumb={<Crumbs items={breadcrumbItems} />}
        eyebrow={data.category}
        title={data.h1}
        subtitle={data.intro}
      >
        <div>
          <p style={{ fontSize: 13.5, color: GREY, margin: '0 0 14px', fontWeight: 600 }}>{metaLine}</p>
          <a href={GUIDES_CTA} style={{ display: 'inline-block', padding: '13px 22px', borderRadius: 12, background: RED, color: '#fff', fontWeight: 700, fontSize: 16, textDecoration: 'none' }}>Book a trusted pro</a>
        </div>
      </Hero>

      {data.takeaways && data.takeaways.length > 0 ? (
        <section style={{ ...wrap, padding: '38px 20px 0' }}>
          <div style={{ border: `1px solid ${BORDER}`, borderLeft: `4px solid ${RED}`, borderRadius: 14, padding: '20px 24px', background: '#fff' }}>
            <h2 style={{ fontSize: 15, fontWeight: 800, letterSpacing: '.6px', textTransform: 'uppercase', color: RED, margin: '0 0 12px' }}>Key takeaways</h2>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 9 }}>
              {data.takeaways.map((t, i) => (
                <li key={i} style={{ display: 'flex', gap: 10, fontSize: 15.5, color: INK, lineHeight: 1.55 }}>
                  <span aria-hidden="true" style={{ color: TEAL, fontWeight: 900, flex: 'none' }}>✓</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      {data.costTable ? (
        <section style={{ ...wrap, padding: '44px 20px 8px' }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 18px' }}>{data.costTable.caption}</h2>
          <div style={{ overflowX: 'auto', border: `1px solid ${BORDER}`, borderRadius: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {data.costTable.head.map((h, i) => (
                    <th key={i} style={{ textAlign: 'left', padding: '14px 18px', fontSize: 14, fontWeight: 700, color: INK, borderBottom: `1px solid ${BORDER}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.costTable.rows.map((r, i) => (
                  <tr key={i}>
                    {r.map((c, j) => (
                      <td key={j} style={{ padding: '13px 18px', fontSize: 15, color: j === 1 ? INK : GREY, fontWeight: j === 1 ? 700 : 400, borderBottom: i < data.costTable.rows.length - 1 ? `1px solid ${BORDER}` : 'none' }}>{c}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 13, color: GREY, margin: '12px 2px 0' }}>Prices are typical U.S. ranges for guidance only. Your actual price depends on your city, the job and the pro.</p>
        </section>
      ) : null}

      {data.checklists && data.checklists.length > 0 ? (
        <section style={{ ...wrap, padding: '40px 20px 4px' }}>
          {data.checklists.map((list, i) => <Checklist key={i} list={list} />)}
          <p style={{ fontSize: 13.5, color: GREY, margin: '2px 2px 0' }}>
            <span style={{ ...chipStyle('diy'), marginRight: 8 }}>DIY</span> you can safely do this yourself
            <span style={{ ...chipStyle('pro'), margin: '0 8px 0 18px' }}>Book a pro</span> worth handing to a background-checked pro
          </p>
        </section>
      ) : null}

      <section style={{ ...wrap, padding: '28px 20px 8px' }}>
        {(data.sections || []).map((s, i) => (
          <div key={i} style={{ marginBottom: 26 }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 10px', color: INK }}>{s.h2}</h2>
            {s.body.map((p, j) => (
              <p key={j} style={{ fontSize: 16, color: GREY, margin: '0 0 12px', lineHeight: 1.7, maxWidth: 760 }}>{p}</p>
            ))}
            {s.tip ? (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: 'rgba(13,148,136,.07)', border: `1px solid rgba(13,148,136,.25)`, borderRadius: 12, padding: '14px 18px', maxWidth: 760 }}>
                <span aria-hidden="true" style={{ fontSize: 18, flex: 'none' }}>💡</span>
                <p style={{ fontSize: 15, color: INK, margin: 0, lineHeight: 1.6 }}><strong style={{ color: TEAL }}>Pro tip:</strong> {s.tip}</p>
              </div>
            ) : null}
          </div>
        ))}
        {data.serviceSlug ? (
          <p style={{ fontSize: 16, margin: '4px 0 8px' }}>
            <a href={`/category?type=all`} style={{ color: RED, fontWeight: 700, textDecoration: 'none' }}>Book {data.service.toLowerCase()} on BestHand →</a>
          </p>
        ) : null}
      </section>

      <section style={{ ...wrap, padding: '10px 20px 34px' }}>
        <EmailCaptureCard guideSlug={slug} guideTitle={data.h1} />
      </section>

      <section style={{ background: LIGHT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ ...wrap, padding: '46px 20px' }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 22px' }}>Frequently asked questions</h2>
          <div style={{ display: 'grid', gap: 14 }}>
            {(data.faqs || []).map(([q, a], i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', border: `1px solid ${BORDER}` }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 8px', color: INK }}>{q}</h3>
                <p style={{ fontSize: 15, color: GREY, margin: 0, lineHeight: 1.6 }}>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 ? (
        <section style={{ ...wrap, padding: '36px 20px' }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 16px' }}>Related guides</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {related.map((o) => (
              <a key={o.slug} href={`/guides/${o.slug}`} style={{ display: 'inline-block', padding: '10px 16px', borderRadius: 999, border: `1px solid ${BORDER}`, background: '#fff', color: INK, fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>{o.h1}</a>
            ))}
            <a href="/guides" style={{ display: 'inline-block', padding: '10px 16px', borderRadius: 999, border: `1px solid ${TEAL}`, background: '#fff', color: TEAL, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>All guides →</a>
          </div>
        </section>
      ) : null}

      {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
      <CtaBand title="Get it done with BestHand" subtitle="Book a background-checked local pro with upfront pricing — pay securely when the job is done." ctaText="Get started" ctaHref={GUIDES_CTA} />
    </div>
  );
}

export default Guide;
